import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import moment from 'moment';
import 'moment/locale/th';
moment.locale('th');

const SUPPORTED_INPUT_FORMATS = ['YYYY-MM-DD', 'YYYY/MM/DD', 'DD/MM/YYYY', 'DD-MM-YYYY', moment.ISO_8601];

const formatDateDisplay = (date) => {
    if (!date) return '';
    const m = Array.isArray(date) ? moment(date[0]) : moment(date, SUPPORTED_INPUT_FORMATS, true);
    const parsed = m.isValid() ? m : moment(date);
    if (!parsed.isValid()) return '';
    const rawYear = parsed.year();
    const beYear = rawYear >= 2400 ? rawYear : rawYear + 543;
    return `${parsed.format('DD/MM')}/${beYear}`;
};

const DatePicker = ({ label, name, value, onChange, disabled }) => {
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarPos, setCalendarPos] = useState({ top: 0, left: 0 });
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // คำนวณตำแหน่งปฏิทินจาก bounding rect ของ input
    const updatePosition = () => {
        if (!inputRef.current) return;
        const rect = inputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const calHeight = 280; // ประมาณความสูงปฏิทิน

        if (spaceBelow < calHeight) {
            // แสดงด้านบน
            setCalendarPos({ top: rect.top + window.scrollY - calHeight - 4, left: rect.left + window.scrollX });
        } else {
            // แสดงด้านล่าง
            setCalendarPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
        }
    };

    const handleOpen = () => {
        if (disabled) return;
        updatePosition();
        setShowCalendar(true);
    };

    useEffect(() => {
        if (!showCalendar) return;
        function handleDocClick(e) {
            if (containerRef.current?.contains(e.target)) return;
            // ตรวจว่า click อยู่ใน portal หรือไม่
            const portal = document.getElementById('__datepicker-portal__');
            if (portal?.contains(e.target)) return;
            setShowCalendar(false);
        }
        document.addEventListener('mousedown', handleDocClick);
        document.addEventListener('touchstart', handleDocClick);
        return () => {
            document.removeEventListener('mousedown', handleDocClick);
            document.removeEventListener('touchstart', handleDocClick);
        };
    }, [showCalendar]);

    // Patch header ปีให้เป็น พ.ศ. ด้วย MutationObserver
    useEffect(() => {
        if (!showCalendar) return;

        const convertToBE = () => {
            const portal = document.getElementById('__datepicker-portal__');
            if (!portal) return;
            portal.querySelectorAll('.rdtSwitch').forEach((sw) => {
                const newText = sw.textContent.replace(/\b(\d{4})\b/g, (y) => {
                    const n = parseInt(y, 10);
                    return n >= 1900 && n <= 2099 ? String(n + 543) : y;
                });
                if (sw.textContent !== newText) sw.textContent = newText;
            });
        };

        const portal = document.getElementById('__datepicker-portal__');
        if (!portal) return;
        const observer = new MutationObserver(convertToBE);
        observer.observe(portal, { childList: true, subtree: true });
        convertToBE();
        return () => observer.disconnect();
    }, [showCalendar]);

    // ปฏิทิน render ผ่าน Portal ออกไปที่ body เพื่อหลีกเลี่ยง overflow clipping
    const calendarPortal = showCalendar
        ? ReactDOM.createPortal(
            <div
                id="__datepicker-portal__"
                style={{
                    position: 'absolute',
                    top: calendarPos.top,
                    left: calendarPos.left,
                    zIndex: 9999,
                }}
            >
                <Datetime
                    className="input-spacing-input-date"
                    name={name}
                    value={
                        value
                            ? moment(value, SUPPORTED_INPUT_FORMATS, true).isValid()
                                ? moment(value, SUPPORTED_INPUT_FORMATS, true)
                                : moment(value)
                            : null
                    }
                    onChange={(date) => {
                        const formatted = moment(date).format('YYYY-MM-DD');
                        onChange(formatted, name);
                        setShowCalendar(false);
                    }}
                    timeFormat={false}
                    locale="th"
                    open={true}
                    closeOnSelect={true}
                    inputProps={{ hidden: true }}
                    renderMonth={(props, month) => (
                        <td {...props}>{moment().month(month).format('MMMM')}</td>
                    )}
                    renderYear={(props, year) => (
                        <td {...props}>{year + 543}</td>
                    )}
                />
            </div>,
            document.body
        )
        : null;

    return (
        <div ref={containerRef}>
            <div className="d-flex align-items-center">
                {label && (
                    <label htmlFor={name} className="form-label">{label}</label>
                )}
                <div className="input-group" style={{ position: 'relative' }}>
                    <input
                        ref={inputRef}
                        type="text"
                        className="form-control input-spacing bg-white"
                        disabled={disabled}
                        readOnly
                        name={name}
                        value={value ? formatDateDisplay(value) : ''}
                        placeholder="กรุณาระบุวันที่"
                        style={{ width: '200px' }}
                        onClick={handleOpen}
                    />
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleOpen}
                        disabled={disabled}
                        style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            height: '100%',
                            padding: '0 10px'
                        }}
                    >
                        <i className="fas fa-calendar"></i>
                    </button>
                </div>
            </div>
            {calendarPortal}
        </div>
    );
};

export default DatePicker;
